import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Form, Select, Spin, Switch } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { ObjectCardSection } from '../../../components/layout/ObjectCard';
import '../../../components/layout/ObjectCardExtras.css';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../../utils/feedback';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  CONFIRMATION_NOTICE_ROLES,
  ConfirmationNoticeRecipientsDto,
  ConfirmationNoticeRole,
  NotificationUserOptionDto,
  fetchConfirmationNoticeRecipients,
  fetchNotificationUserOptions,
  saveConfirmationNoticeRecipients,
} from '../api/confirmationNoticeRecipientsApi';


/**
 * ADMIN card: recipients of the e-mail sent after a customer order is
 * confirmed (stock level per line). Default = every ADMIN and OPERATIONS user.
 */
const ConfirmationNoticeRecipientsCard: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const [config, setConfig] = useState<ConfirmationNoticeRecipientsDto | null>(null);
  const [users, setUsers] = useState<NotificationUserOptionDto[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([fetchConfirmationNoticeRecipients(), fetchNotificationUserOptions()])
      .then(([loadedConfig, loadedUsers]) => {
        if (!active) return;
        setConfig(loadedConfig);
        setUsers(loadedUsers);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => { active = false; };
  }, []);

  // One wide section on the notification settings page (ObjectCard primitives).
  const frame = (body: React.ReactNode, actions?: React.ReactNode) => (
    <ObjectCardSection
      wide
      title={t('notifications:confirmationNotice.title')}
      icon={<MailOutlined />}
      tone="green"
      hint={t('notifications:confirmationNotice.description')}
      actions={actions}
    >
      {body}
    </ObjectCardSection>
  );

  if (loadFailed) {
    return frame(
      <Alert type="error" showIcon message={t('notifications:confirmationNotice.loadFailed')} />,
    );
  }

  if (!config) {
    return frame(<Spin />);
  }

  const nobodySelected = config.includeRoles.length === 0 && !config.includeAccountManager && config.userIds.length === 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveConfirmationNoticeRecipients(config);
      setConfig(saved);
      feedback.success(t('notifications:confirmationNotice.saved'));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:confirmationNotice.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return frame(
    <>
      <Form layout="vertical" component="div">
        <div className="obj-form">
          <Form.Item label={t('notifications:confirmationNotice.roles')}>
            <Checkbox.Group
              value={config.includeRoles}
              onChange={(values) => setConfig({
                ...config,
                includeRoles: CONFIRMATION_NOTICE_ROLES.filter((role) => (values as string[]).includes(role)),
              })}
              options={CONFIRMATION_NOTICE_ROLES.map((role: ConfirmationNoticeRole) => ({
                value: role,
                label: t(`notifications:confirmationNotice.role.${role}`),
              }))}
            />
          </Form.Item>
          <Form.Item className="tool-form__end" label={t('notifications:confirmationNotice.accountManager')}>
            <Switch
              aria-label={t('notifications:confirmationNotice.accountManager')}
              checked={config.includeAccountManager}
              onChange={(checked) => setConfig({ ...config, includeAccountManager: checked })}
            />
          </Form.Item>
          <Form.Item label={t('notifications:confirmationNotice.chosenUsers')}>
            <Select
              mode="multiple"
              aria-label={t('notifications:confirmationNotice.chosenUsers')}
              placeholder={t('notifications:confirmationNotice.chosenUsersPlaceholder')}
              value={config.userIds}
              onChange={(userIds: string[]) => setConfig({ ...config, userIds })}
              optionFilterProp="label"
              maxTagCount={3}
              options={users.map((user) => ({
                value: user.id,
                label: `${user.name} (${t(`common:userRole.${user.role}`, { defaultValue: user.role })})`,
              }))}
            />
          </Form.Item>
        </div>
      </Form>
      {nobodySelected && (
        <Alert className="tool-form__alert" type="warning" showIcon message={t('notifications:confirmationNotice.nobody')} />
      )}
    </>,
    <Button type="primary" size="small" loading={saving} onClick={handleSave}>
      {t('notifications:confirmationNotice.save')}
    </Button>,
  );
};

export default ConfirmationNoticeRecipientsCard;
